<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Availability } from '@/types/booking-api';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { convertOctoPrice, formatPrice } from '@/utils/utils';

const props = defineProps<{
  session: Availability;
  isSelected: boolean;
  price: { price: number; currency: 'EUR' | 'USD'; currencyPrecision: number };
  timezone: string;
}>();

const emit = defineEmits<{
  select: [session: Availability];
}>();

const { t } = useI18n();
const { currentLocale } = storeToRefs(useGlobalConfigurationStore());

const formattedPrice = computed(() => {
  const amount = convertOctoPrice(props.price.price, props.price.currencyPrecision);
  // Show cents only when needed: 25 € stays 25 €, 2.5 € becomes 2,50 €
  return formatPrice(amount, props.price.currency, !Number.isInteger(amount));
});

function extractTime(localDateTimeStart: string): string {
  const date = new Date(localDateTimeStart);
  return date.toLocaleTimeString(currentLocale.value, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: props.timezone,
  });
}

function getSessionClasses() {
  if (!props.session.available) {
    return { unavailable: true };
  }

  if (props.session.status === 'LIMITED') {
    return { limited: true, selected: props.isSelected };
  }

  return { available: true, selected: props.isSelected };
}

function shouldShowLimitedBadge(): boolean {
  return props.session.available && props.session.status === 'LIMITED' && props.session.vacancies !== null && props.session.vacancies > 0;
}

function handleClick() {
  if (!props.session.available)
    return;
  emit('select', props.session);
}
</script>

<template>
  <button
    class="session-block"
    :class="getSessionClasses()"
    :disabled="!session.available"
    @click="handleClick"
  >
    <span v-if="session.available" class="price-badge">
      {{ formattedPrice }}
    </span>

    <span class="time-text">
      {{ extractTime(session.localDateTimeStart) }}
    </span>

    <span
      v-if="shouldShowLimitedBadge()"
      class="limited-badge"
      role="status"
    >
      {{ t('booking.limitedPlaces', { count: session.vacancies }) }}
    </span>
  </button>
</template>

<style scoped lang="css">
.session-block {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: var(--yb-spacing-sm);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: 8px;
  background-color: var(--yb-color-white);
  cursor: pointer;
  min-height: 74px;
  max-height: 74px;
}

.session-block.available:hover {
  border-color: var(--yb-color-primary);
}

.session-block.selected {
  border-color: var(--yb-color-primary);
  border-width: 2px;
}

.session-block.limited {
  background-color: var(--yb-color-white);
}

.session-block.unavailable {
  background-color: #f5f5f5;
  cursor: not-allowed;
  opacity: 0.6;
}

.session-block.unavailable::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom right,
    transparent calc(50% - 1px),
    var(--yb-color-darkgray) calc(50% - 1px),
    var(--yb-color-darkgray) calc(50% + 1px),
    transparent calc(50% + 1px)
  );
  pointer-events: none;
  border-radius: 8px;
}

.time-text {
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-color-black);
  position: relative;
  z-index: 1;
}

.session-block.selected .time-text {
  color: var(--yb-color-primary);
}

.session-block.unavailable .time-text {
  color: var(--yb-color-darkgray);
}

.limited-badge {
  background-color: #ffdada;
  color: #ea3532;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
}

.price-badge {
  position: absolute;
  top: 4px;
  right: 4px;
  font-size: var(--yb-text-xs);
  font-weight: var(--yb-text-semi-bold);
  padding: 2px 6px;
  border-radius: 4px;
  background-color: var(--yb-color-light-primary);
  color: var(--yb-color-primary);
  z-index: 2;
}

.session-block.selected .price-badge {
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);
}
</style>
